import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DepartureRow } from '../models/departureRow';
import { compareRows } from '@shared/utils/departureOrArrivalRowComparer';
import speak from '@shared/utils/speak';
import { DepartureOrArrival } from '@shared/models/departureOrArrival';
import { formatCountdown } from '@shared/utils/formatCountdown';
import {
  DEPARTURE_ANNOUNCEMENT_LIFETIME_IN_MILLISECONDS,
  MINUTES_BETWEEN_REPEATED_ANNOUNCEMENT_VOICE,
  VOICED_DEPARTURES_PRIORITY,
} from '@shared/constants';
import useManuallyTriggeredAudio from '@shared/hooks/useManuallyTriggeredAudio';
import useAudio from '@shared/hooks/useAudio';
import useFeatures from '@shared/hooks/useFeatures';
import { play } from '@shared/utils/play';
import { DeepReadonly } from 'ts-essentials';
import { DepartureOrArrivalTimeFormat } from '@shared/models/departureOrArrivalTimeFormat';
import { timeToClockFormat } from '@shared/utils/time';
import { getPlatformName } from '@shared/utils/platformName';
import { TransportMode } from '@shared/models/transportMode';

export interface DepartureAnnouncement {
  id: number;
  text: string;
}

function hasChangedPlatform(
  departureRow: DeepReadonly<DepartureRow>,
  isShowingPlatforms: boolean,
): boolean {
  return (
    (isShowingPlatforms && departureRow.originalPlatform !== undefined) ||
    (!isShowingPlatforms && departureRow.platform !== undefined)
  );
}

function shouldBeAnnounced(
  departureRow: DeepReadonly<DepartureRow>,
  isShowingPlatforms: boolean,
): boolean {
  return (
    departureRow.next.isCancelled ||
    hasChangedPlatform(departureRow, isShowingPlatforms)
  );
}

function shouldOnlyUseChangedPlatform(
  departureRow: DeepReadonly<DepartureRow>,
  isShowingPlatforms: boolean,
  isAnnouncement: boolean,
): boolean {
  return (
    hasChangedPlatform(departureRow, isShowingPlatforms) &&
    (!isShowingPlatforms || (isAnnouncement && !departureRow.next.isCancelled))
  );
}

function getDepartureRowIdentifierText(
  departureRow: DeepReadonly<DepartureRow>,
  isStartOfSentence: boolean,
): string {
  return `${isStartOfSentence ? 'Linje' : 'linje'} ${
    departureRow.line.shortName
  } mot ${
    departureRow.destination.name +
    (departureRow.destination.via ? ` via ${departureRow.destination.via}` : '')
  }`;
}

function getDepartureTimeIdentifier(
  departureRow: DeepReadonly<DepartureRow>,
  timeFormat: DepartureOrArrivalTimeFormat,
): string {
  if (
    timeFormat === 'countdown' ||
    departureRow.next.targetTime === undefined
  ) {
    return '';
  }

  return ` med avgångstid ${timeToClockFormat(departureRow.next.targetTime)}`;
}

function getDepartureRowText(
  departureRow: DeepReadonly<DepartureRow>,
  isStartOfSentence: boolean,
  isShowingPlatforms: boolean,
  isAnnouncement: boolean,
  timeFormat: DepartureOrArrivalTimeFormat,
): string {
  let departureRowText = getDepartureRowIdentifierText(
    departureRow,
    isStartOfSentence,
  );

  if (
    shouldOnlyUseChangedPlatform(
      departureRow,
      isShowingPlatforms,
      isAnnouncement,
    )
  ) {
    departureRowText += ` ändrad till ${getPlatformName(departureRow.transportMode)} ${departureRow.platform}.`;
  } else if (departureRow.next.isCancelled) {
    departureRowText += `${getDepartureTimeIdentifier(departureRow, timeFormat)} är inställd.`;

    if (departureRow.thereafter && !departureRow.thereafter.isCancelled) {
      departureRowText += ` Avgång därefter ${getDepartureText(
        departureRow.thereafter,
        departureRow.transportMode,
        departureRow.originalPlatform,
        departureRow.platform,
        isShowingPlatforms,
        timeFormat,
      )}`;
    }
  } else {
    departureRowText +=
      ' ' +
      getDepartureText(
        departureRow.next,
        departureRow.transportMode,
        departureRow.originalPlatform,
        departureRow.platform,
        isShowingPlatforms,
        timeFormat,
      );
  }
  return departureRowText;
}

function getDepartureTimeText(
  departure: DepartureOrArrival,
  timeFormat: DepartureOrArrivalTimeFormat,
): string {
  if (timeFormat === 'countdown') {
    const departureTime = formatCountdown(departure.time);
    return typeof departureTime === 'number'
      ? `om ${departureTime} ${departureTime > 1 ? 'minuter' : 'minut'}`
      : `går ${departureTime}`;
  }

  const departureTime = timeToClockFormat(departure.time);
  const targetDepartureTime =
    departure.targetTime !== undefined
      ? timeToClockFormat(departure.targetTime)
      : undefined;
  if (
    targetDepartureTime !== undefined &&
    targetDepartureTime !== departureTime
  ) {
    return `med avgångstid ${targetDepartureTime} går ${departureTime}`;
  }

  return `går ${departureTime}`;
}

function getDepartureText(
  departure: DepartureOrArrival,
  transportMode: TransportMode,
  originalPlatform: string | undefined,
  platform: string | undefined,
  isShowingPlatforms: boolean,
  timeFormat: DepartureOrArrivalTimeFormat,
): string {
  const timeText = getDepartureTimeText(departure, timeFormat);

  const platformText =
    isShowingPlatforms && platform
      ? ` ${originalPlatform ? 'ändrad till' : 'från'} ${getPlatformName(transportMode)} ${platform}`
      : '';

  const accessibilityText = !departure.isWheelchairAccessible
    ? ', OBS trappa'
    : '';

  return `${timeText}${platformText}${accessibilityText}.`;
}

function getNextDepartureRow(
  departureRows: DeepReadonly<DepartureRow[]>,
  voicedDepartureRowIds: Set<string>,
): DeepReadonly<DepartureRow> | undefined {
  return departureRows.find(
    (departureRow) => !voicedDepartureRowIds.has(departureRow.id),
  );
}

function getDepartureRowAnnouncement(
  departureRow: DeepReadonly<DepartureRow>,
  isShowingPlatforms: boolean,
  timeFormat: DepartureOrArrivalTimeFormat,
): string | undefined {
  const departureRowHasChangedPlatform = hasChangedPlatform(
    departureRow,
    isShowingPlatforms,
  );
  if (
    (departureRow.next.isCancelled && departureRow.thereafter?.isCancelled) ||
    shouldOnlyUseChangedPlatform(departureRow, isShowingPlatforms, true)
  ) {
    return getDepartureRowText(
      departureRow,
      true,
      isShowingPlatforms,
      true,
      timeFormat,
    );
  } else if (departureRow.next.isCancelled || departureRowHasChangedPlatform) {
    return `${timeFormat === 'countdown' ? 'Nästa avgång med ' : ''}${getDepartureRowText(
      departureRow,
      timeFormat === 'time',
      isShowingPlatforms,
      true,
      timeFormat,
    )}`;
  }

  return undefined;
}

function isChangedToCancelled(
  newDepartureRow: DeepReadonly<DepartureRow>,
  previousDepartureRow: DeepReadonly<DepartureRow>,
): boolean {
  const nextHasCancelledDeparture = newDepartureRow.next.isCancelled;
  const thereafterHasCancelledDeparture =
    newDepartureRow.thereafter?.isCancelled === true;

  const nextHadCancelledDeparture = previousDepartureRow.next.isCancelled;
  const thereafterHadCancelledDeparture =
    previousDepartureRow.thereafter?.isCancelled === true;

  return (
    (nextHasCancelledDeparture && !nextHadCancelledDeparture) ||
    (nextHasCancelledDeparture &&
      nextHadCancelledDeparture &&
      thereafterHasCancelledDeparture &&
      !thereafterHadCancelledDeparture)
  );
}

function shouldAnnounceChangedPlatform(
  newDepartureRow: DeepReadonly<DepartureRow>,
  previousDepartureRow: DeepReadonly<DepartureRow>,
  isShowingPlatforms: boolean,
): boolean {
  return (
    newDepartureRow.platform !== previousDepartureRow.platform &&
    ((isShowingPlatforms && newDepartureRow.originalPlatform !== undefined) ||
      (!isShowingPlatforms && newDepartureRow.platform !== undefined))
  );
}

export default function useDepartureAnnouncements(
  departureRows: DeepReadonly<DepartureRow[]>,
  isShowingPlatforms: boolean,
  timeFormat: DepartureOrArrivalTimeFormat,
): DepartureAnnouncement[] {
  const { playMessage } = useAudio();
  const { pushAudio } = useFeatures();
  const currentSortedDepartureRows = useRef<DeepReadonly<DepartureRow[]>>([]);
  const previousSortedDepartureRows = useRef<DeepReadonly<DepartureRow>[]>([]);
  const announcementsTimeoutId = useRef<number | undefined>(undefined);
  const [announcements, setAnnouncements] = useState<DepartureAnnouncement[]>(
    [],
  );
  const nextAnnouncementId = useRef(0);
  const sortedDepartureRows = useMemo(
    () =>
      [...departureRows].sort((first, second) =>
        compareRows(first, second, 'time'),
      ),
    [departureRows],
  );

  // Mirrors a voiced departure change event into the ARIA live region for screen readers,
  // independently of whether the departure is also spoken out loud.
  const announce = useCallback((text: string) => {
    const id = nextAnnouncementId.current++;
    setAnnouncements((current) => [...current, { id, text }]);
    window.setTimeout(() => {
      setAnnouncements((current) =>
        current.filter((announcement) => announcement.id !== id),
      );
    }, DEPARTURE_ANNOUNCEMENT_LIFETIME_IN_MILLISECONDS);
  }, []);

  async function voiceDepartures() {
    async function tryVoiceNextDepartureRow(
      voicedDepartureRowIds: Set<string>,
    ) {
      const departureRow = getNextDepartureRow(
        currentSortedDepartureRows.current,
        voicedDepartureRowIds,
      );
      if (!departureRow) {
        return Promise.resolve();
      }

      voicedDepartureRowIds.add(departureRow.id);
      await speak(
        getDepartureRowText(
          departureRow,
          true,
          isShowingPlatforms,
          false,
          timeFormat,
        ),
      );
      await tryVoiceNextDepartureRow(voicedDepartureRowIds);
    }

    const voicedDepartureRowIds = new Set<string>();
    await tryVoiceNextDepartureRow(voicedDepartureRowIds);
  }

  const voiceAllAnnouncements = useCallback(async () => {
    for (const departureRow of currentSortedDepartureRows.current) {
      const announcement = getDepartureRowAnnouncement(
        departureRow,
        isShowingPlatforms,
        timeFormat,
      );

      if (!announcement) {
        continue;
      }

      await speak(announcement);
    }
  }, [isShowingPlatforms, timeFormat]);

  const tryStopRepeatingAnnouncements = useCallback(() => {
    if (
      currentSortedDepartureRows.current.some((departureRow) =>
        shouldBeAnnounced(departureRow, isShowingPlatforms),
      )
    ) {
      return false;
    }

    window.clearTimeout(announcementsTimeoutId.current);
    announcementsTimeoutId.current = undefined;
    return true;
  }, [isShowingPlatforms]);

  const tryStartRepeatingAnnouncements = useCallback(() => {
    if (announcementsTimeoutId.current) {
      return;
    }

    if (tryStopRepeatingAnnouncements()) {
      return;
    }

    async function voiceAnnouncementsAndRepeat() {
      await playMessage(() => voiceAllAnnouncements());
      announcementsTimeoutId.current = undefined;
      tryStartRepeatingAnnouncements();
    }

    announcementsTimeoutId.current = window.setTimeout(
      () => {
        void voiceAnnouncementsAndRepeat();
      },
      MINUTES_BETWEEN_REPEATED_ANNOUNCEMENT_VOICE * 60 * 1000,
    );
  }, [playMessage, tryStopRepeatingAnnouncements, voiceAllAnnouncements]);

  const voiceAnnouncements = useCallback(
    async (departureRowIds: string[]) => {
      if (
        !departureRowIds.some((departureRowId) => {
          const departureRow = currentSortedDepartureRows.current.find(
            (departureRowCandidate) =>
              departureRowCandidate.id === departureRowId,
          );

          if (!departureRow) {
            return false;
          }

          return shouldBeAnnounced(departureRow, isShowingPlatforms);
        })
      ) {
        return;
      }

      await play('push');
      for (const departureRowId of departureRowIds) {
        const departureRow = currentSortedDepartureRows.current.find(
          (departureRowCandidate) =>
            departureRowCandidate.id === departureRowId,
        );

        if (!departureRow) {
          continue;
        }

        const announcement = getDepartureRowAnnouncement(
          departureRow,
          isShowingPlatforms,
          timeFormat,
        );

        if (!announcement) {
          continue;
        }

        await speak(announcement);
      }

      tryStartRepeatingAnnouncements();
    },
    [isShowingPlatforms, timeFormat, tryStartRepeatingAnnouncements],
  );

  useEffect(() => {
    currentSortedDepartureRows.current = sortedDepartureRows;

    const findPreviousDepartureRow = (
      newDepartureRow: DeepReadonly<DepartureRow>,
    ) =>
      previousSortedDepartureRows.current.find(
        (previousDepartureRowCandidate) =>
          previousDepartureRowCandidate.id === newDepartureRow.id,
      );

    const departureRowsToAnnounce = sortedDepartureRows.filter(
      (newDepartureRow) => {
        const previousDepartureRow = findPreviousDepartureRow(newDepartureRow);
        if (!previousDepartureRow) {
          return false;
        }

        return (
          isChangedToCancelled(newDepartureRow, previousDepartureRow) ||
          shouldAnnounceChangedPlatform(
            newDepartureRow,
            previousDepartureRow,
            isShowingPlatforms,
          )
        );
      },
    );

    for (const departureRow of departureRowsToAnnounce) {
      const announcement = getDepartureRowAnnouncement(
        departureRow,
        isShowingPlatforms,
        timeFormat,
      );
      if (announcement) {
        announce(announcement);
      }
    }

    if (pushAudio) {
      const hasDepartureRowsToAnnounce = sortedDepartureRows.some(
        (departureRow) => shouldBeAnnounced(departureRow, isShowingPlatforms),
      );

      if (departureRowsToAnnounce.length > 0) {
        void playMessage(() =>
          voiceAnnouncements(
            departureRowsToAnnounce.map((departureRow) => departureRow.id),
          ),
        );
      } else if (hasDepartureRowsToAnnounce) {
        tryStartRepeatingAnnouncements();
      } else {
        tryStopRepeatingAnnouncements();
      }
    }

    previousSortedDepartureRows.current = sortedDepartureRows;
  }, [
    announce,
    pushAudio,
    isShowingPlatforms,
    playMessage,
    sortedDepartureRows,
    timeFormat,
    tryStartRepeatingAnnouncements,
    tryStopRepeatingAnnouncements,
    voiceAnnouncements,
  ]);

  useEffect(() => {
    if (!pushAudio) {
      return;
    }

    return () => {
      window.clearTimeout(announcementsTimeoutId.current);
    };
  }, [pushAudio]);

  useManuallyTriggeredAudio(VOICED_DEPARTURES_PRIORITY, voiceDepartures);

  return announcements;
}
